import { Component, Inject, OnInit, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';

/** Una configuración de un equipo del catálogo de Equipos. */
export interface EquipmentConfiguration {
  name:                string;
  description:         string;
  accessories:         string[]; // ids (subconjunto de los del equipo)
  peripheralEquipment: string[]; // ids (subconjunto de los del equipo)
  gasTanks:            string[]; // ids (subconjunto de los del equipo)
}

/** Datos inyectados en el diálogo de configuraciones. */
export interface EquipmentConfigurationsDialogData {
  identifier:              string;   // code/model del equipo (título)
  configuration:           any[];    // arreglo actual de configuraciones
  // Ids compatibles del propio equipo:
  accessoryIds:            string[];
  peripheralIds:           string[];
  gasTankIds:              string[];
  // Catálogos completos para resolver etiquetas:
  accessoriesList:         any[];    // {id, code, description}
  peripheralEquipmentList: any[];    // {id, code, description}
  gasTanksList:            any[];    // {id, code, gas}
}

@Component({
  selector: 'app-equipment-configurations-dialog',
  standalone: true,
  templateUrl: './equipment-configurations-dialog.component.html',
  styleUrls: ['./equipment-configurations-dialog.component.scss'],
  imports: [FormsModule, MatDialogModule, MaterialModule],
})
export class EquipmentConfigurationsDialogComponent implements OnInit {

  rows = signal<EquipmentConfiguration[]>([]);

  // Opciones limitadas a los ids compatibles del propio equipo.
  accessoryOptions:  any[] = [];
  peripheralOptions: any[] = [];
  gasTankOptions:    any[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EquipmentConfigurationsDialogData,
    public dialogRef: MatDialogRef<EquipmentConfigurationsDialogComponent>,
  ) {}

  ngOnInit(): void {
    // Deep-clone de las configuraciones de entrada para no mutar el original.
    const list = (this.data.configuration ?? []).map((c: any) => ({
      name:                c?.name ?? '',
      description:         c?.description ?? '',
      accessories:         Array.isArray(c?.accessories) ? [...c.accessories] : [],
      peripheralEquipment: Array.isArray(c?.peripheralEquipment) ? [...c.peripheralEquipment] : [],
      gasTanks:            Array.isArray(c?.gasTanks) ? [...c.gasTanks] : [],
    }));
    this.rows.set(list);

    const accessoryIds = new Set(this.data.accessoryIds ?? []);
    const peripheralIds = new Set(this.data.peripheralIds ?? []);
    const gasTankIds = new Set(this.data.gasTankIds ?? []);

    this.accessoryOptions  = (this.data.accessoriesList ?? []).filter(a => accessoryIds.has(a.id));
    this.peripheralOptions = (this.data.peripheralEquipmentList ?? []).filter(p => peripheralIds.has(p.id));
    this.gasTankOptions    = (this.data.gasTanksList ?? []).filter(g => gasTankIds.has(g.id));
  }

  // ─── Etiquetas ──────────────────────────────────────────────────────────
  accessoryLabel(a: any): string {
    return a?.code ? `${a.code}${a?.description ? ' — ' + a.description : ''}` : (a?.description ?? '');
  }
  peripheralLabel(p: any): string {
    return p?.code ? `${p.code}${p?.description ? ' — ' + p.description : ''}` : (p?.description ?? '');
  }
  gasTankLabel(g: any): string {
    return g?.code ? `${g.code}${g?.gas ? ' · ' + g.gas : ''}` : (g?.gas ?? '');
  }

  // ─── Edición de filas ───────────────────────────────────────────────────
  add(): void {
    this.rows.set([
      ...this.rows(),
      { name: '', description: '', accessories: [], peripheralEquipment: [], gasTanks: [] },
    ]);
  }

  remove(index: number): void {
    const list = [...this.rows()];
    list.splice(index, 1);
    this.rows.set(list);
  }

  patch(index: number, key: keyof EquipmentConfiguration, value: any): void {
    const list = [...this.rows()];
    list[index] = { ...list[index], [key]: value };
    this.rows.set(list);
  }

  // ─── Guardar / Cancelar ─────────────────────────────────────────────────
  save(): void {
    // Descartamos filas sin nombre.
    const cleaned = this.rows()
      .filter(r => (r.name ?? '').trim().length > 0)
      .map(r => ({
        name:                r.name.trim(),
        description:         r.description ?? '',
        accessories:         [...(r.accessories ?? [])],
        peripheralEquipment: [...(r.peripheralEquipment ?? [])],
        gasTanks:            [...(r.gasTanks ?? [])],
      }));
    this.dialogRef.close(cleaned);
  }

  close(): void { this.dialogRef.close(undefined); }
}
